import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

export function GoogleButton() {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <button type="button" onClick={start} data-testid="google-signin-btn" className="w-full flex items-center justify-center gap-3 border border-[#E5E5E5] hover:border-[#0a0a0a] transition-colors px-8 py-4 text-sm">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.4 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 34.9 26.7 36 24 36c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39.6 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l6.3 5.3C40.9 35.5 44 30.1 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
      <span className="font-medium">Continue with Google</span>
    </button>
  );
}

export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const [status, setStatus] = useState("Processing...");
  useEffect(() => {
    // Session ID is in URL hash fragment
    const hash = window.location.hash || loc.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { setStatus("No session found"); const t = setTimeout(() => nav("/login"), 1500); return () => clearTimeout(t); }
    const session_id = m[1];
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        if (cancelled) return;
        toast.success(`Welcome, ${data.user.name}`);
        window.history.replaceState(null, "", "/dashboard");
        nav("/dashboard");
      } catch (e) {
        if (cancelled) return;
        logger.error("Google sign-in failed:", e);
        setStatus("Google sign-in failed");
        setTimeout(() => nav("/login"), 2000);
      }
    })();
    return () => { cancelled = true; };
  }, [loc.hash, nav]);
  return (
    <section className="max-w-[520px] mx-auto px-6 py-24 text-center">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Google</p>
      <h1 className="font-serif text-4xl mt-4">{status}</h1>
    </section>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch { toast.error("Failed. Try again."); }
    finally { setLoading(false); }
  };
  if (sent) return (
    <section className="max-w-[520px] mx-auto px-6 py-24 text-center">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Check Your Inbox</p>
      <h1 className="font-serif text-4xl mt-4">Reset link sent.</h1>
      <p className="mt-6 text-[#555]">If an account exists for {email}, we've sent you a reset link.</p>
    </section>
  );
  return (
    <section className="max-w-[520px] mx-auto px-6 py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Reset Password</p>
      <h1 className="font-serif text-5xl mt-2">Forgot your password?</h1>
      <p className="mt-4 text-[#555]">Enter your email and we'll send you a link to reset it.</p>
      <form onSubmit={submit} className="mt-10 space-y-6">
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Email</label>
          <input data-testid="forgot-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>
        <button data-testid="forgot-submit" disabled={loading} className="w-full bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold">{loading ? "Sending..." : "Send Reset Link"}</button>
      </form>
    </section>
  );
}

export function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    if (!token) { setErr("Missing token"); return; }
    try { await api.post("/auth/reset-password", { token, password }); setDone(true); setTimeout(() => nav("/login"), 1500); }
    catch (e) { setErr(e.response?.data?.detail || "Reset failed"); }
  };
  if (done) return (
    <section className="max-w-[520px] mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Password reset.</h1>
      <p className="mt-4 text-[#555]">Redirecting to login…</p>
    </section>
  );
  return (
    <section className="max-w-[520px] mx-auto px-6 py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Reset Password</p>
      <h1 className="font-serif text-5xl mt-2">Choose a new password.</h1>
      <form onSubmit={submit} className="mt-10 space-y-6">
        <div><label className="text-xs font-mono tracking-[0.3em] uppercase">New Password</label>
          <input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" /></div>
        <div><label className="text-xs font-mono tracking-[0.3em] uppercase">Confirm Password</label>
          <input data-testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" /></div>
        {err && <p className="text-sm text-[#9A1F1F]" data-testid="reset-error">{err}</p>}
        <button data-testid="reset-submit" className="w-full bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold">Reset Password</button>
      </form>
    </section>
  );
}
